import React, { useState } from 'react';
import { Dialog } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useToast } from '../../context/ToastContext';

export function EmailInfoModal({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  const { showToast } = useToast();
  const [showCopied, setShowCopied] = useState(false);

  const copyEmail = async () => {
    try {
      await navigator.clipboard.writeText('notes@careercard.ai');
      setShowCopied(true);
      showToast('Email address copied!', 'success');
      setTimeout(() => setShowCopied(false), 2000);
    } catch (err) {
      showToast('Failed to copy email address', 'error');
    }
  };

  return (
    <Dialog open={isOpen} onClose={onClose} className="relative z-50">
      <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
      
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel className="mx-auto max-w-2xl w-full max-h-[90vh] rounded-2xl bg-gray-900 overflow-y-auto">
          {/* Close Button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 z-10 w-8 h-8 bg-white/10 rounded-full flex items-center justify-center text-white hover:bg-white/20 transition-colors"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>

          {/* Hero Section */}
          <div className="relative bg-gradient-to-br from-purple-600 to-purple-800 text-white p-8 text-center overflow-hidden">
            {/* Background decoration */}
            <div className="absolute -top-20 -right-20 w-64 h-64 bg-white/10 rounded-full blur-3xl" />
            
            <div className="relative z-10">
              <h2 className="text-2xl font-bold mb-3">✨ Turn emails into notes instantly</h2>
              <p className="text-base opacity-90 mb-6">
                Send emails to create notes automatically - perfect for capturing thoughts on-the-go
              </p>
              
              {/* Email Address */}
              <div className="relative inline-block">
                <button
                  onClick={copyEmail}
                  className="bg-white/20 hover:bg-white/30 transition-all transform hover:scale-105 px-6 py-3 rounded-xl font-mono text-lg cursor-pointer"
                >
                  notes@careercard.ai
                  <div className="text-xs opacity-80 mt-1 font-sans">Click to copy</div>
                </button>
                
                {/* Copied tooltip */}
                {showCopied && (
                  <div className="absolute -top-10 left-1/2 transform -translate-x-1/2 bg-green-500 text-white px-3 py-1 rounded text-sm">
                    Copied!
                  </div>
                )}
              </div>
              
              {/* Steps */}
              <div className="flex justify-center gap-6 mt-8">
                <div className="text-center">
                  <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center text-lg font-bold mb-2 mx-auto">
                    1
                  </div>
                  <div className="text-xs opacity-90">Add & verify<br />your email</div>
                </div>
                <div className="text-center">
                  <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center text-lg font-bold mb-2 mx-auto">
                    2
                  </div>
                  <div className="text-xs opacity-90">Send email to<br />notes@careercard.ai</div>
                </div>
                <div className="text-center">
                  <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center text-lg font-bold mb-2 mx-auto">
                    3
                  </div>
                  <div className="text-xs opacity-90">Your note appears<br />instantly</div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Modal Body */}
          <div className="p-6 bg-gray-800">
            {/* Example Section */}
            <div className="mb-6">
              <h3 className="text-base font-semibold text-white mb-3 flex items-center gap-2">
                <span>📝</span> Example Email
              </h3>
              <div className="bg-gray-900 border border-gray-700 rounded-lg p-4 font-mono text-sm text-gray-300">
                <div className="text-gray-400">Subject: Meeting with John about Q1 goals</div>
                <div className="mt-3">
                  Discussed the following priorities:<br />
                  - Launch new feature by March<br />
                  - Increase user engagement 20%<br />
                  - Hire 2 new engineers<br />
                  <br />
                  Next steps: Schedule follow-up for next week<br />
                  <br />
                  #meeting #goals #q1-planning @i 4 @e 5
                </div>
              </div>
            </div>
            
            {/* Tips Section */}
            <div>
              <h3 className="text-base font-semibold text-white mb-3 flex items-center gap-2">
                <span>💡</span> Tips for best results
              </h3>
              <ul className="space-y-2 text-gray-300 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-green-500 mt-0.5">✓</span>
                  <span>Email subject becomes your note title</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-500 mt-0.5">✓</span>
                  <span>Email body becomes the note content</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-500 mt-0.5">✓</span>
                  <span>Add <span className="font-mono bg-gray-700 px-1 rounded">#hashtags</span> anywhere in your email - they automatically become note tags</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-500 mt-0.5">✓</span>
                  <div>
                    <span>Set career impact with <span className="font-mono bg-gray-700 px-1 rounded">@i [1-5]</span></span>
                    <div className="text-xs text-gray-400 mt-1 ml-2">
                      1 = Minimal Impact, 2 = Minor Impact, 3 = Moderate Impact,<br />
                      4 = Significant Impact, 5 = Major Impact
                    </div>
                  </div>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-500 mt-0.5">✓</span>
                  <div>
                    <span>Set engagement level with <span className="font-mono bg-gray-700 px-1 rounded">@e [1-5]</span></span>
                    <div className="text-xs text-gray-400 mt-1 ml-2">
                      1 = Draining, 2 = Disengaging, 3 = Neutral,<br />
                      4 = Engaging, 5 = Energizing
                    </div>
                  </div>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-500 mt-0.5">✓</span>
                  <span>Each email can send up to 5 notes per day</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-500 mt-0.5">✓</span>
                  <span>Notes are automatically linked to the work experience for that email address</span>
                </li>
              </ul>
            </div>
          </div>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
}